import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Upload, RefreshCw, Loader2, ChevronDown, ChevronRight, ExternalLink, AlertTriangle, MapPin, Trash2, UserPlus } from "lucide-react";
import { api } from "../lib/api";
import { SchoolLogo, AcademicSnapshot, StaffTable, LevelPill, KenPomStrip, LEVEL_COLORS, DivisionTag, DIVISION_SHORT, ratingLabel } from "./CoachDbParts";

const STATUS = {
  ok: { label: "Emails", cls: "text-emerald-300 border-emerald-500/40" },
  no_emails: { label: "Names only", cls: "text-amber-300 border-amber-500/40" },
  failed: { label: "Needs manual", cls: "text-red-300 border-red-500/40" },
  running: { label: "Scraping…", cls: "text-[#3aa0ff] border-[#3aa0ff]/40" },
  pending: { label: "Pending", cls: "text-[#757575] border-white/15" },
};

// Coach Database — every D1 program with scraped coaching staff + academic snapshot. Org admins only.
export default function CoachDatabaseSection() {
  const [data, setData] = useState({ programs: [], facets: { conferences: [], states: [] }, total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [conference, setConference] = useState("");
  const [state, setState] = useState("");
  const [status, setStatus] = useState("");
  const [level, setLevel] = useState("");
  const [division, setDivision] = useState("");
  const [sport, setSport] = useState("mens");
  const sportRef = useRef("mens");
  useEffect(() => { sportRef.current = sport; }, [sport]);
  const [sort, setSort] = useState("");
  const [kp, setKp] = useState(null);
  const [kpSyncing, setKpSyncing] = useState(false);
  const [job, setJob] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setData(await api.coachDbPrograms({ q: q || undefined, conference: conference || undefined, state: state || undefined, status: status || undefined, level: level || undefined, sort: sort || undefined, division: division || undefined, sport })); }
    catch { toast.error("Could not load coach database"); }
    finally { setLoading(false); }
  }, [q, conference, state, status, level, sort, division, sport]);

  useEffect(() => { api.coachDbStatus(sport).then(setJob).catch(() => {}); }, [sport]);
  const cloneWomens = async () => {
    try { const r = await api.coachDbCloneWomens(); toast.success(`Created ${r.created} women's programs`); setSport("womens"); await startScrape("pending", "womens"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create women's programs"); }
  };

  useEffect(() => { api.coachDbKenpomStatus().then(setKp).catch(() => {}); }, []);
  const [d3Syncing, setD3Syncing] = useState(false);
  const syncD3 = async () => {
    setD3Syncing(true);
    try { const r = await api.coachDbD3Sync(sport); toast.success(`D3 Datacast (${sport === "womens" ? "WBB" : "MBB"}): ${r.matched} of ${r.teams} teams matched`, { description: r.unmatched?.length ? `${r.unmatched.length} teams not in your CSV` : undefined }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "D3 Datacast sync failed"); }
    finally { setD3Syncing(false); }
  };
  const syncKenpom = async () => {
    setKpSyncing(true);
    try {
      const r = await api.coachDbKenpomSync();
      toast.success(`KenPom ${r.season - 1}-${String(r.season).slice(2)}: ${r.matched} of ${r.kenpom_teams} teams matched`, { description: r.programs_without_kenpom?.length ? `${r.programs_without_kenpom.length} programs without KenPom data` : undefined });
      setKp(await api.coachDbKenpomStatus()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "KenPom sync failed"); }
    finally { setKpSyncing(false); }
  };

  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const s = await api.coachDbStatus(sportRef.current); if (alive) setJob(s); if (s.running && alive) setTimeout(tick, 2500); else if (alive && s.finished_at) load(); }
      catch { /* ignore */ }
    };
    tick();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScrape = async (scope, sp = sport) => {
    try { const s = await api.coachDbScrape({ scope, sport: sp }); setJob(s); toast.success(`Scraping ${s.total} programs in the background`); pollUntilDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start scrape"); }
  };
  const pollUntilDone = () => {
    const tick = async () => { const s = await api.coachDbStatus(sportRef.current); setJob(s); if (s.running) setTimeout(tick, 2500); else load(); };
    setTimeout(tick, 1500);
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImporting(true);
    try { const r = await api.coachDbImport(f); toast.success(`Imported ${r.created} new · ${r.updated} updated · ${r.total} total`); load(); const s = await api.coachDbStatus(); setJob(s); }
    catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const counts = job?.counts || {};
  const progress = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="coach-db-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex border border-white/15 mb-2" data-testid="coach-db-sport">
            {[["mens", "Men's Basketball"], ["womens", "Women's Basketball"]].map(([k, label]) => (
              <button key={k} onClick={() => { setSport(k); setOpenId(null); }} className={`h-9 px-4 text-[10px] uppercase tracking-[0.25em] font-bold ${sport === k ? "bg-white text-[#121212]" : "text-[#a0a0a0] hover:text-white"}`} style={{ transition: "background-color .15s ease, color .15s ease" }} data-testid={`coach-db-sport-${k}`}>{label}</button>
            ))}
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#757575]" data-testid="coach-db-summary">
            {job?.total_programs || data.total} programs · <span className="text-emerald-300">{counts.ok || 0} with emails</span> · <span className="text-amber-300">{counts.no_emails || 0} names only</span> · <span className="text-red-300">{counts.failed || 0} need manual</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} data-testid="coach-db-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} className="h-9 px-3 border border-white/15 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-white flex items-center gap-1.5 disabled:opacity-50" data-testid="coach-db-import-btn">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import CSV
          </button>
          {sport === "womens" && data.facets.womens_count === 0 && (
            <button onClick={cloneWomens} className="h-9 px-4 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5" data-testid="coach-db-clone-womens-btn">
              <UserPlus className="w-3.5 h-3.5" /> Build women's database from men's schools
            </button>
          )}
          <button onClick={syncD3} disabled={d3Syncing} title="D3 efficiency ratings from d3datacast.com" className="h-9 px-3 border border-white/15 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-white flex items-center gap-1.5 disabled:opacity-40" data-testid="coach-db-d3-sync-btn">
            {d3Syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync D3 Ratings
          </button>
          {sport === "mens" && <button onClick={syncKenpom} disabled={kpSyncing || !kp?.configured} title={kp?.configured ? (kp.last_sync ? `Last sync ${new Date(kp.last_sync).toLocaleDateString()}` : "Pull KenPom ratings") : "Add KENPOM_API_KEY to enable"} className="h-9 px-3 border border-white/15 text-[10px] uppercase tracking-widest text-[#a0a0a0] hover:text-white flex items-center gap-1.5 disabled:opacity-40" data-testid="coach-db-kenpom-sync-btn">
            {kpSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync KenPom{kp?.synced ? ` (${kp.synced})` : ""}
          </button>}
          <button onClick={() => startScrape("pending")} disabled={job?.running || !((counts.failed || 0) + (counts.pending || 0) + (counts.running || 0))} className={`h-9 px-3 border text-[10px] uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40 ${(counts.pending || 0) + (counts.running || 0) > 0 && !job?.running ? "border-amber-500/50 text-amber-300 hover:text-white" : "border-white/15 text-[#a0a0a0] hover:text-white"}`} data-testid="coach-db-retry-btn">
            <RefreshCw className="w-3.5 h-3.5" /> {(counts.pending || 0) + (counts.running || 0) > 0 && !job?.running ? `Resume (${(counts.pending || 0) + (counts.running || 0) + (counts.failed || 0)} left)` : "Retry failed"}
          </button>
          <button onClick={() => startScrape("all")} disabled={job?.running} className="h-9 px-4 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="coach-db-scrape-all-btn">
            {job?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {job?.running ? "Scraping…" : "Re-scrape all"}
          </button>
        </div>
      </div>

      {!job?.running && ((counts.pending || 0) + (counts.running || 0)) > 0 && (
        <div className="border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200 flex items-center gap-2" data-testid="coach-db-resume-notice">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {(counts.pending || 0) + (counts.running || 0)} programs haven't been scraped yet{job?.stale ? " — the last run was interrupted by a server restart" : ""}. Hit Resume to pick up where it left off.
        </div>
      )}
      {job?.running && (
        <div className="border border-[#3aa0ff]/30 bg-[#3aa0ff]/5 p-4" data-testid="coach-db-progress">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-[#3aa0ff]">
            <span>Scraping staff directories</span><span>{job.done} / {job.total} · {progress}%</span>
          </div>
          <div className="mt-2 h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-[#3aa0ff] shadow-[0_0_12px_#3aa0ff]" style={{ width: `${progress}%`, transition: "width .6s ease" }} /></div>
          <div className="mt-1.5 text-[10px] text-[#757575]">{job.ok} succeeded · {job.failed} failed so far — you can keep browsing, results fill in as they land.</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#757575] absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search school, conference, city or coach name…" className="w-full h-10 pl-9 pr-3 bg-[#0e0e0e] border border-white/15 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]" data-testid="coach-db-search" />
        </div>
        {(data.facets.divisions || []).length > 1 && (
          <div className="inline-flex border border-white/15" data-testid="coach-db-division">
            {["", ...data.facets.divisions].map(d => (
              <button key={d || "all"} onClick={() => setDivision(d)} className={`h-10 px-3 text-[10px] uppercase tracking-widest font-bold ${division === d ? "bg-[var(--org-accent)] text-[#121212]" : "text-[#a0a0a0] hover:text-white"}`} style={{ transition: "background-color .15s ease, color .15s ease" }} data-testid={`coach-db-division-${(d ? DIVISION_SHORT[d] || d : "all").toLowerCase()}`}>{d ? DIVISION_SHORT[d] || d : "All"}</button>
            ))}
          </div>
        )}
        <select value={level} onChange={e => setLevel(e.target.value)} className="h-10 px-3 bg-[#0e0e0e] border border-white/15 text-xs text-white focus:outline-none" style={{ borderColor: level ? LEVEL_COLORS[level] : undefined }} data-testid="coach-db-level">
          <option value="">All levels</option>{(data.facets.levels || []).map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={conference} onChange={e => setConference(e.target.value)} className="h-10 px-3 bg-[#0e0e0e] border border-white/15 text-xs text-white focus:outline-none max-w-[260px]" data-testid="coach-db-conference">
          <option value="">All conferences</option>{data.facets.conferences.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={state} onChange={e => setState(e.target.value)} className="h-10 px-3 bg-[#0e0e0e] border border-white/15 text-xs text-white focus:outline-none" data-testid="coach-db-state">
          <option value="">All states</option>{data.facets.states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} className="h-10 px-3 bg-[#0e0e0e] border border-white/15 text-xs text-white focus:outline-none" data-testid="coach-db-status">
          <option value="">Any status</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} className="h-10 px-3 bg-[#0e0e0e] border border-white/15 text-xs text-white focus:outline-none" data-testid="coach-db-sort">
          <option value="">A → Z</option>
          <option value="kenpom">Rating rank</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[var(--org-accent)]" /></div>
      ) : !data.programs.length ? (
        <div className="border border-dashed border-white/10 py-16 text-center text-sm text-[#757575]" data-testid="coach-db-empty">
          {data.total === 0 && !q && !conference && !state && !status ? (sport === "womens" ? "No women's programs yet — use the button above to build them from the men's schools." : "No programs yet — the bundled database loads automatically; import a CSV to add more schools.") : "No programs match those filters."}
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/[0.06] bg-[#0f0f0f]" data-testid="coach-db-list">
          {data.programs.map(p => (
            <ProgramRow key={p.id} program={p} open={openId === p.id} onToggle={() => setOpenId(openId === p.id ? null : p.id)} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

// Pick a player -> creates a linked Recruitment Tracker row pre-filled from this program.
function AddToTracker({ program }) {
  const [open, setOpen] = useState(false);
  const [players, setPlayers] = useState(null);
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && !players) api.listPlayers().then(list => {
      const sorted = [...list].sort((a, b) => (a.league === "HS" ? 0 : 1) - (b.league === "HS" ? 0 : 1) || a.name.localeCompare(b.name));
      setPlayers(sorted); setPid(sorted[0]?.id || "");
    }).catch(() => toast.error("Could not load players"));
  }, [open, players]);
  const add = async () => {
    if (!pid) return;
    setBusy(true);
    const head = (program.staff || []).find(s => /head coach/i.test(s.title || "") && !/associate|assistant/i.test(s.title || ""));
    try {
      await api.createRecruitment(pid, {
        college_name: program.short_name || program.school, program_id: program.id, interest_level: "Watching",
        level: program.division === "NCAA D3" ? (program.level === "High Academic" ? "D3 High Academic" : "NCAA D3") : (program.division && program.division !== "NCAA D1" ? program.division : (program.level ? `D1 ${program.level}` : "NCAA D1")),
        league: program.conference || null,
        coach_name: head?.name || null, coach_contact: head?.email || null, last_contact_date: null, scholarship_type: null, notes: null,
      });
      const pl = players.find(p => p.id === pid);
      toast.success(`${program.short_name || program.school} added to ${pl?.name}'s tracker`);
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  return (
    <span className="relative inline-flex">
      <button onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1 h-7 px-2 border border-[var(--org-accent)]/40 text-[var(--org-accent)] hover:bg-[var(--org-accent)]/10" data-testid="coach-db-add-tracker-btn"><UserPlus className="w-3 h-3" /> Add to tracker</button>
      {open && (
        <div className="absolute z-30 top-8 left-0 w-72 bg-[#0e0e0e] border border-white/15 shadow-2xl p-3 space-y-2 normal-case tracking-normal" data-testid="coach-db-add-tracker-popover">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">Add {program.short_name || program.school} to</div>
          {!players ? <Loader2 className="w-4 h-4 animate-spin text-[#757575]" /> : (
            <select value={pid} onChange={e => setPid(e.target.value)} className="w-full h-9 px-2 bg-[#101010] border border-white/15 text-sm text-white focus:outline-none" data-testid="coach-db-add-tracker-player">
              {players.map(p => <option key={p.id} value={p.id}>{p.name}{p.league ? ` · ${p.league}` : ""}</option>)}
            </select>
          )}
          <div className="flex gap-2">
            <button onClick={add} disabled={busy || !pid} className="flex-1 h-8 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-widest font-bold disabled:opacity-50" data-testid="coach-db-add-tracker-confirm">{busy ? "Adding…" : "Add school"}</button>
            <button onClick={() => setOpen(false)} className="h-8 px-3 border border-white/15 text-[10px] uppercase tracking-widest text-[#a0a0a0]" data-testid="coach-db-add-tracker-cancel">Cancel</button>
          </div>
        </div>
      )}
    </span>
  );
}

function ProgramRow({ program, open, onToggle, onChanged }) {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  const st = STATUS[program.scrape_status] || STATUS.pending;

  useEffect(() => { if (open && !full) api.coachDbProgram(program.id).then(setFull).catch(() => {}); }, [open, full, program.id]);
  useEffect(() => { if (open) setFull(null); }, [program.scraped_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const rescrape = async () => {
    setBusy(true);
    try { const r = await api.coachDbRescrape(program.id); setFull(r); onChanged(); toast[r.scrape_status === "failed" ? "warning" : "success"](r.scrape_status === "failed" ? `Still no luck: ${r.scrape_error}` : `${program.short_name || program.school}: ${r.staff.length} staff pulled`); }
    catch { toast.error("Re-scrape failed"); } finally { setBusy(false); }
  };
  const saveStaff = async (staff) => { const r = await api.coachDbUpdateProgram(program.id, { staff }); setFull(r); onChanged(); };
  const remove = async () => {
    if (!window.confirm(`Remove ${program.school} from the database?`)) return;
    try { await api.coachDbDeleteProgram(program.id); onChanged(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`coach-db-row-${program.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02]" data-testid={`coach-db-toggle-${program.id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-[#757575] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#757575] shrink-0" />}
        <SchoolLogo program={program} size={36} />
        <div className="min-w-0 flex-1">
          <div className="font-heading text-lg text-white uppercase tracking-tight truncate">{program.school}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] truncate flex items-center gap-1.5">
            <DivisionTag division={program.division} />
            <LevelPill level={program.level} />
            <span className="text-[var(--org-accent)]">{program.conference}</span>
            <span className="text-[#555]">·</span><MapPin className="w-3 h-3 text-[#555]" />{program.city}, {program.state_code}
          </div>
        </div>
        {program.kenpom?.rank != null && (
          <div className="hidden md:block text-right shrink-0" data-testid={`coach-db-kenpom-${program.id}`}>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#757575]">{ratingLabel(program.kenpom.source)}</div>
            <div className="font-heading text-lg text-white leading-none">#{program.kenpom.rank} <span className="text-xs text-[#757575]">· {program.kenpom.wins}-{program.kenpom.losses}</span></div>
          </div>
        )}
        <div className="hidden sm:block text-right shrink-0">
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#757575]">Staff</div>
          <div className="font-heading text-lg text-white leading-none">{program.staff_count} <span className="text-xs text-[#757575]">· {program.email_count} emails</span></div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 h-6 px-2 border text-[9px] uppercase tracking-[0.2em] font-bold ${st.cls}`} data-testid={`coach-db-status-${program.id}`}>
          {program.scrape_status === "failed" && <AlertTriangle className="w-3 h-3" />}{st.label}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-5 pt-1 pl-[68px] space-y-4" data-testid={`coach-db-detail-${program.id}`}>
          {!full ? <div className="py-4"><Loader2 className="w-4 h-4 animate-spin text-[#757575]" /></div> : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest">
                <a href={full.landing_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-7 px-2 border border-white/10 text-[#a0a0a0] hover:text-white" data-testid="coach-db-site-link"><ExternalLink className="w-3 h-3" /> Team site</a>
                {full.source_url && <a href={full.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-7 px-2 border border-white/10 text-[#a0a0a0] hover:text-white" data-testid="coach-db-source-link"><ExternalLink className="w-3 h-3" /> Staff page</a>}
                <AddToTracker program={full} />
                <button onClick={rescrape} disabled={busy} className="inline-flex items-center gap-1 h-7 px-2 border border-white/10 text-[#a0a0a0] hover:text-white disabled:opacity-50" data-testid="coach-db-rescrape-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Re-scrape</button>
                <button onClick={remove} className="inline-flex items-center gap-1 h-7 px-2 border border-white/10 text-[#757575] hover:text-red-300" data-testid="coach-db-delete-btn"><Trash2 className="w-3 h-3" /> Remove</button>
                <span className="ml-auto text-[#555] normal-case tracking-normal">{full.scraped_at ? `Verified ${new Date(full.scraped_at).toLocaleDateString()}` : "Not scraped yet"}{full.scrape_error ? ` · ${full.scrape_error}` : ""}</span>
              </div>
              <KenPomStrip kenpom={full.kenpom} />
              <AcademicSnapshot academics={full.academics} program={full} />
              <StaffTable staff={full.staff} editable onSave={saveStaff} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
